/**
 * Shortcut notation, parsing and matching (BUILD-PLAN 3.6). Pure: the
 * global key listener and the `?` sheet both build on this.
 *
 * Notation (lowercase, as in module manifests):
 *   "mod+k"     Ctrl+K on Windows/Linux, Cmd+K on macOS
 *   "shift+p"   chord with modifiers
 *   "g s"       sequence: G, then S within SEQUENCE_TIMEOUT_MS
 *   "?"         symbol keys match regardless of Shift
 *   "escape", "enter", "arrowup", "f2", "/"
 */

export const SEQUENCE_TIMEOUT_MS = 1200;

export interface KeyStep {
  key: string;
  mod: boolean;
  ctrl: boolean;
  meta: boolean;
  alt: boolean;
  shift: boolean;
}

export interface ParsedShortcut {
  /** Normalised notation, e.g. "mod+k" or "g s". */
  id: string;
  steps: KeyStep[];
}

const MODIFIERS = new Set(['mod', 'ctrl', 'meta', 'cmd', 'alt', 'option', 'shift']);
const KEY_ALIASES: Record<string, string> = {
  esc: 'escape',
  return: 'enter',
  space: ' ',
  spacebar: ' ',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  del: 'delete',
  comma: ',',
  slash: '/',
  question: '?',
};

function normaliseKey(key: string): string {
  const k = key.toLowerCase();
  return KEY_ALIASES[k] ?? k;
}

function parseStep(raw: string): KeyStep {
  const parts = raw.split('+').map((p) => p.trim().toLowerCase());
  // "shift++" would be odd; a trailing empty part means the key was "+".
  let key = parts.pop() ?? '';
  if (key === '' && parts.length > 0) key = '+';
  const step: KeyStep = {
    key: normaliseKey(key),
    mod: false,
    ctrl: false,
    meta: false,
    alt: false,
    shift: false,
  };
  for (const m of parts) {
    if (!MODIFIERS.has(m)) throw new Error(`unknown modifier "${m}" in shortcut "${raw}"`);
    if (m === 'mod') step.mod = true;
    else if (m === 'ctrl') step.ctrl = true;
    else if (m === 'meta' || m === 'cmd') step.meta = true;
    else if (m === 'alt' || m === 'option') step.alt = true;
    else step.shift = true;
  }
  if (!step.key) throw new Error(`shortcut "${raw}" has no key`);
  return step;
}

function stepId(s: KeyStep): string {
  const mods = [
    s.mod && 'mod',
    s.ctrl && 'ctrl',
    s.meta && 'meta',
    s.alt && 'alt',
    s.shift && 'shift',
  ].filter(Boolean);
  return [...mods, s.key === ' ' ? 'space' : s.key].join('+');
}

export function parseShortcut(notation: string): ParsedShortcut {
  const raw = notation.trim();
  if (!raw) throw new Error('empty shortcut');
  // A lone "+" or " " key would be split away; handle the common symbols.
  const tokens = raw === ' ' ? [' '] : raw.split(/\s+/);
  const steps = tokens.map(parseStep);
  return { id: steps.map(stepId).join(' '), steps };
}

/** Letters and digits respect Shift; symbols ("?", "/") ignore it (layout dependent). */
function isSymbol(key: string): boolean {
  return key.length === 1 && !/[a-z0-9]/.test(key);
}

export interface KeyEventLike {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

export function stepMatches(step: KeyStep, e: KeyEventLike, isMac: boolean): boolean {
  if (normaliseKey(e.key) !== step.key) return false;
  const wantCtrl = step.ctrl || (step.mod && !isMac);
  const wantMeta = step.meta || (step.mod && isMac);
  if (e.ctrlKey !== wantCtrl || e.metaKey !== wantMeta || e.altKey !== step.alt) return false;
  if (!isSymbol(step.key) && e.shiftKey !== step.shift) return false;
  return true;
}

/** True when the step uses Ctrl/Cmd/Alt, so it may fire while typing in a field. */
export function isChord(step: KeyStep): boolean {
  return step.mod || step.ctrl || step.meta || step.alt;
}

/** Labels for <Kbd>: "mod+k" -> ["Ctrl", "K"]; sequences flatten to their keys. */
export function shortcutLabels(notation: string, isMac: boolean): string[] {
  const { steps } = parseShortcut(notation);
  const out: string[] = [];
  for (const s of steps) {
    if (s.mod) out.push(isMac ? 'Cmd' : 'Ctrl');
    if (s.ctrl) out.push('Ctrl');
    if (s.meta) out.push(isMac ? 'Cmd' : 'Win');
    if (s.alt) out.push(isMac ? 'Option' : 'Alt');
    if (s.shift) out.push('Shift');
    out.push(keyLabel(s.key));
  }
  return out;
}

const KEY_LABELS: Record<string, string> = {
  escape: 'Esc',
  enter: 'Enter',
  ' ': 'Space',
  arrowup: 'Up',
  arrowdown: 'Down',
  arrowleft: 'Left',
  arrowright: 'Right',
  delete: 'Del',
  backspace: 'Backspace',
  tab: 'Tab',
};

function keyLabel(key: string): string {
  if (KEY_LABELS[key]) return KEY_LABELS[key];
  if (/^f\d{1,2}$/.test(key)) return key.toUpperCase();
  return key.length === 1 ? key.toUpperCase() : key[0]?.toUpperCase() + key.slice(1);
}

export function isSequence(notation: string): boolean {
  return parseShortcut(notation).steps.length > 1;
}

/* ---------- matcher ---------- */

export interface Binding<T> {
  shortcut: ParsedShortcut;
  /** Fire even when focus is in a text field (default: only chords do). */
  allowInInputs?: boolean;
  value: T;
}

export type MatchResult<T> =
  { kind: 'match'; binding: Binding<T> } | { kind: 'pending' } | { kind: 'none' };

/**
 * Feeds key events and reports complete matches. Sequences ("g s") wait
 * for the next key until SEQUENCE_TIMEOUT_MS. Later bindings for the same
 * shortcut win (route scope registers after global).
 */
export class ShortcutMatcher<T> {
  private buffer: KeyEventLike[] = [];
  private lastAt = 0;

  constructor(private readonly isMac: boolean) {}

  feed(
    e: KeyEventLike,
    bindings: readonly Binding<T>[],
    now: number,
    typing: boolean,
  ): MatchResult<T> {
    if (['shift', 'control', 'alt', 'meta'].includes(e.key.toLowerCase()))
      return { kind: 'pending' };
    if (now - this.lastAt > SEQUENCE_TIMEOUT_MS) this.buffer = [];
    this.lastAt = now;

    const attempt = (buffer: KeyEventLike[]): MatchResult<T> => {
      let pending = false;
      let found: Binding<T> | undefined;
      for (const b of bindings) {
        const { steps } = b.shortcut;
        if (steps.length < buffer.length) continue;
        if (typing && !b.allowInInputs && !steps.every(isChord)) continue;
        const prefix = buffer.every((ev, i) => {
          const s = steps[i];
          return s !== undefined && stepMatches(s, ev, this.isMac);
        });
        if (!prefix) continue;
        if (steps.length === buffer.length) found = b;
        else pending = true;
      }
      if (found) return { kind: 'match', binding: found };
      return pending ? { kind: 'pending' } : { kind: 'none' };
    };

    let result = attempt([...this.buffer, e]);
    if (result.kind === 'none' && this.buffer.length > 0) {
      // The sequence broke; the key may start a new one.
      result = attempt([e]);
      this.buffer = result.kind === 'pending' ? [e] : [];
      return result;
    }
    this.buffer = result.kind === 'pending' ? [...this.buffer, e] : [];
    return result;
  }

  reset(): void {
    this.buffer = [];
  }
}

/**
 * True when a list box is a popup (a menu-like overlay), not a list that
 * lives in the page. Popups: Radix poppers, anything marked open
 * (data-state="open") or data-popup, and lists an expanded combobox or
 * button controls. In-page lists (the notes queue, the plan list, chart
 * code results) are where people click to pick a row, so the page's
 * single-key shortcuts must keep working there.
 */
function isPopupListbox(list: Element, doc: Document | null): boolean {
  if (list.matches('[data-state="open"], [data-popup]')) return true;
  if (list.closest('[data-radix-popper-content-wrapper], [data-radix-menu-content]')) return true;
  const id = list.getAttribute('id');
  if (!id || !doc) return false;
  const esc = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(id) : id;
  return (
    doc.querySelector(
      `[aria-expanded="true"][aria-controls~="${esc}"], [aria-expanded="true"][aria-owns~="${esc}"]`,
    ) != null
  );
}

/**
 * True when the key goes to an open menu or popup list box (the office
 * switcher's type-ahead: typing "mid" must not fire the page's D shortcut,
 * AM-03). Single-key shortcuts wait until it closes. In-page list boxes do
 * not count: J/K in the notes queue and E/1-3 in the plan list still fire.
 */
export function menuTarget(target: EventTarget | null, doc: Document | null = null): boolean {
  const d = doc ?? (typeof document === 'undefined' ? null : document);
  const el =
    target && typeof (target as HTMLElement).closest === 'function'
      ? (target as HTMLElement)
      : null;
  if (el?.closest('[role="menu"], [role="menubar"], [data-radix-popper-content-wrapper]')) {
    return true;
  }
  const list = el?.closest('[role="listbox"]');
  if (list && isPopupListbox(list, el?.ownerDocument ?? d)) return true;
  return (
    d?.querySelector(
      '[role="menu"][data-state="open"], [role="listbox"][data-state="open"], [data-radix-menu-content][data-state="open"]',
    ) != null
  );
}

/** True when keyboard focus is in a field that takes text. */
export function isTypingTarget(target: EventTarget | null): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== 'string') return false;
  const el = target as HTMLElement;
  if (el.isContentEditable) return true;
  const tag = el.tagName.toLowerCase();
  if (tag === 'textarea' || tag === 'select') return true;
  if (tag !== 'input') return false;
  const type = (el as HTMLInputElement).type;
  return !['checkbox', 'radio', 'button', 'submit', 'reset', 'range', 'color'].includes(type);
}

const NATIVE_ARROW_KEYS = new Set([
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
  'PageUp',
  'PageDown',
]);
const OWNS_ACTIVATE = new Set([
  'button',
  'link',
  'tab',
  'radio',
  'switch',
  'checkbox',
  'menuitem',
  'menuitemradio',
  'menuitemcheckbox',
  'option',
  'combobox',
  'treeitem',
]);
const OWNS_NAVIGATE = new Set([
  'tab',
  'radio',
  'slider',
  'menuitem',
  'menuitemradio',
  'menuitemcheckbox',
  'option',
  'combobox',
  'listbox',
  'spinbutton',
  'treeitem',
  'scrollbar',
]);

function implicitRole(el: HTMLElement): string | null {
  const explicit = el.getAttribute('role');
  if (explicit) return explicit;
  const tag = el.tagName.toLowerCase();
  if (tag === 'button' || tag === 'summary') return 'button';
  if (tag === 'a' && el.hasAttribute('href')) return 'link';
  if (tag === 'select') return 'combobox';
  if (tag !== 'input') return null;
  const type = (el as HTMLInputElement).type;
  if (type === 'range') return 'slider';
  if (type === 'checkbox') return 'checkbox';
  if (type === 'radio') return 'radio';
  if (type === 'button' || type === 'submit' || type === 'reset') return 'button';
  if (type === 'number') return 'spinbutton';
  return null;
}

/**
 * True when the focused control handles this plain key itself: Enter and
 * Space on buttons, links, tabs and other activatable controls; arrows,
 * Home/End and PageUp/PageDown on sliders, tabs, radios, lists and menus.
 * Single-key shortcuts must not take these keys away from the control.
 */
export function controlOwnsKey(target: EventTarget | null, key: string): boolean {
  if (!target || typeof (target as HTMLElement).tagName !== 'string') return false;
  const role = implicitRole(target as HTMLElement);
  if (role === null) return false;
  if (key === 'Enter' || key === ' ') return OWNS_ACTIVATE.has(role);
  if (NATIVE_ARROW_KEYS.has(key)) return OWNS_NAVIGATE.has(role);
  return false;
}
